import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useFeatures } from "@/context/FeaturesContext";
import { useI18n } from "@/lib/i18n";
import { HubAddress } from "@/components/HubAddress";
import Navbar from "@/components/Navbar";
import { CountrySearchDialog, CustomsWarningCard, useGeoCountries } from "@/components/GeoDialogs";
import { ProhibitedNotice, hasProhibitedAck } from "@/components/ProhibitedGoods";
import { DestCity } from "@/components/DestCityCI";
import { ModeDeparture } from "@/components/ModeDeparture";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ShoppingBag, MapPin, Copy, ExternalLink, PackagePlus, ArrowRight, Store, Globe, Sparkles } from "lucide-react";

const CATEGORIES = ["Toutes", "Mode", "Électronique", "Beauté", "Maison", "Sport", "Marketplace", "Autres"];
const BUY_COUNTRIES = ["Royaume-Uni", "France", "Allemagne", "Espagne", "Belgique", "Italie", "Pays-Bas", "USA", "Chine", "Turquie", "Émirats (Dubaï)", "Autre pays"];

export default function ShopShip() {
  const { user } = useAuth();
  const { isOn } = useFeatures();
  const { t, lang } = useI18n();
  const navigate = useNavigate();
  const [buy, setBuy] = useState("");
  const [route, setRoute] = useState(null);
  const [address, setAddress] = useState(null);
  const [shops, setShops] = useState([]);
  const [cat, setCat] = useState("Toutes");
  const [declOpen, setDeclOpen] = useState(false);
  const [declShop, setDeclShop] = useState("");
  const [otherOpen, setOtherOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [aiQ, setAiQ] = useState("");
  const [aiBusy, setAiBusy] = useState(false);
  const [aiRes, setAiRes] = useState(null);
  // Destinations officielles : Pays & villes (Actif = Oui ET Destination = Oui)
  const [dests, setDests] = useState([]);
  useEffect(() => { api.get("/countries?kind=destination").then(({ data }) => setDests(data)).catch(() => setDests([])); }, []);

  const askAi = async () => {
    if (aiQ.trim().length < 2) return;
    setAiBusy(true); setAiRes(null);
    try {
      const { data } = await api.post("/shopship/assistant", { query: aiQ.trim() });
      setAiRes(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    setAiBusy(false);
  };
  const [customsAck, setCustomsAck] = useState(false);
  const [prohibitedOk, setProhibitedOk] = useState(hasProhibitedAck());
  const geo = useGeoCountries();
  const cLabel = (name) => {
    const k = `ctry.${name}`, v = t(k);
    if (v !== k) return v;
    const g = geo.find((x) => x.name === name);
    return lang === "en" && g ? g.name_en : name;
  };

  // Si le client clique un pays avant que la session soit chargée, on relance le routage
  React.useEffect(() => {
    if (buy && user?.role === "customer" && !route) pick(buy);
  }, [user]); // eslint-disable-line

  const pick = async (c) => {
    setBuy(c); setRoute(null); setAddress(null); setCat("Toutes"); setCustomsAck(false);
    api.get(`/shopship/shops-for?buy_country=${encodeURIComponent(c)}`)
      .then(({ data }) => setShops(data.shops || [])).catch(() => setShops([]));
    if (user?.role === "customer") {
      try {
        const { data: r } = await api.get(`/shopship/route-address?buy_country=${encodeURIComponent(c)}`);
        setRoute(r);
        const { data: a } = await api.post(`/shopship/countries/${encodeURIComponent(r.address_country)}/activate`);
        const main = a.addresses.find((x) => x.warehouse_id === r.warehouse?.id) || a.addresses[0];
        setAddress(main ? { ...main, client_code: a.client_code } : null);
      } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    }
  };

  const copyAddress = (a) => {
    const txt = `${a.recipient}\n${a.address}\n${a.postal_code} ${a.city}\n${a.country}${a.phone ? `\nTél : ${a.phone}` : ""}`;
    navigator.clipboard.writeText(txt).then(() => toast.success(t("ss.copiedAddr")));
  };
  const copyInstructions = (a) => {
    const txt = `MON ADRESSE DE LIVRAISON — Colis2Go Achats en ligne\n\nDestinataire : ${a.recipient}\nAdresse : ${a.address}\n${a.postal_code} ${a.city}, ${a.country}${a.phone ? `\nTéléphone : ${a.phone}` : ""}\n\nIMPORTANT : ${a.instructions}\nHoraires du Hub : ${a.hours}`;
    navigator.clipboard.writeText(txt).then(() => toast.success(t("ss.copiedInstr")));
  };

  if (!isOn("shop_and_ship")) {
    return (
      <div className="min-h-screen bg-background"><Navbar />
        <div className="p-16 text-center text-muted-foreground">Le service Achats en ligne n’est pas disponible pour le moment.</div>
      </div>
    );
  }

  const filteredShops = cat === "Toutes" ? shops : shops.filter((s) => s.category === cat);

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="bg-primary text-white">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 py-12">
          <div className="inline-flex items-center gap-2 border border-accent/50 bg-accent/10 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-accent mb-4">
            <ShoppingBag className="h-3.5 w-3.5" /> {t("ss.badge")}
          </div>
          <h1 className="font-heading font-extrabold text-3xl sm:text-4xl tracking-tight">{t("ss.title")}</h1>
          <p className="mt-3 text-white/80 max-w-2xl">{t("ss.subtitle")}</p>
        </div>
      </div>

      <div className="mx-auto max-w-6xl px-4 sm:px-6 py-10">
        {/* Étape 1 : pays d'achat */}
        <h2 className="font-heading font-bold text-lg flex items-center gap-2"><Globe className="h-5 w-5 text-accent" /> {t("ss.step1")}</h2>
        <div className="mt-3 flex flex-wrap gap-2">
          {BUY_COUNTRIES.map((c) => {
            const active = buy === c || (c === "Autre pays" && !!buy && !BUY_COUNTRIES.includes(buy));
            return (
              <button key={c} data-testid={`buy-country-${c}`} onClick={() => (c === "Autre pays" ? setSearchOpen(true) : pick(c))}
                className={`px-4 py-2 text-sm font-semibold border transition-colors ${active ? "bg-primary text-white border-primary" : "border-border bg-card hover:border-accent"}`}>
                {c === "Autre pays" && active ? cLabel(buy) : t(`ctry.${c}`)}
              </button>
            );
          })}
        </div>

        {/* Étape 2 : adresse assignée */}
        {buy && (
          <div className="mt-10">
            <h2 className="font-heading font-bold text-lg">{t("ss.step2")}</h2>
            {!user && (
              <div className="mt-3 border border-accent/40 bg-accent/5 p-5 flex flex-wrap items-center justify-between gap-3">
                <span className="text-sm">{t("ss.loginPrompt")}</span>
                <Button data-testid="ss-login-btn" className="rounded-sm bg-accent text-white" onClick={() => navigate("/login")}>{t("ss.loginBtn")}</Button>
              </div>
            )}
            {user && user.role !== "customer" && <p className="mt-3 text-sm text-muted-foreground">{t("ss.customersOnly")}</p>}
            {route && (
              <p className="mt-3 font-semibold text-accent" data-testid="route-message">{t("ss.routeMsg").replace("{c}", cLabel(buy)).replace("{label}", route.address_country === "France" ? "France" : "UK")}</p>
            )}
            {route?.customs_warning && !customsAck && <CustomsWarningCard onConfirm={() => setCustomsAck(true)} />}
            {address && (!route?.customs_warning || customsAck) && (
              <div data-testid="route-address-card" className="mt-3 border border-border bg-card p-6 max-w-xl">
                <div className="text-xs font-bold uppercase tracking-wider text-accent">{t("ss.clientCode")} <span data-testid="route-c2g-code">{address.client_code}</span></div>
                <div className="mt-3 font-heading font-bold text-lg">{address.recipient}</div>
                <div className="mt-1"><HubAddress hub={address} /></div>
                {address.instructions && <p className="mt-2 text-xs text-muted-foreground">{address.instructions}</p>}
                <div className="mt-4 flex flex-wrap gap-2">
                  <ProhibitedNotice value={prohibitedOk} onChange={setProhibitedOk} />
                  <Button data-testid="ss-copy-address" size="sm" disabled={!prohibitedOk} className="mt-2 rounded-sm bg-primary text-white gap-1.5" onClick={() => copyAddress(address)}><Copy className="h-4 w-4" /> {t("ss.copyAddress")}</Button>
                  <Button data-testid="ss-copy-instructions" size="sm" variant="outline" className="rounded-sm gap-1.5" onClick={() => copyInstructions(address)}><Copy className="h-4 w-4" /> {t("ss.copyInstructions")}</Button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Boutiques */}
        {buy && (
          <div className="mt-10">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <h2 className="font-heading font-bold text-lg">{user?.role === "customer" ? t("ss.step4Customer") : t("ss.step3Guest")}</h2>
              {user?.role === "customer" && (
                <Button data-testid="ss-declare-btn" className="rounded-sm bg-accent text-white gap-1.5" onClick={() => { setDeclShop(""); setDeclOpen(true); }}>
                  <PackagePlus className="h-4 w-4" /> {t("ss.declared")}
                </Button>
              )}
            </div>
            {/* Assistant Achat IA */}
            <div className="mt-4 border-2 border-accent/40 bg-accent/5 p-4" data-testid="ai-assistant-block">
              <div className="font-heading font-bold flex items-center gap-2"><Sparkles className="h-4 w-4 text-accent" /> {t("ai.q")}</div>
              <div className="mt-2 flex flex-wrap gap-2 max-w-2xl">
                <Input data-testid="ai-shop-input" className="rounded-sm flex-1 min-w-[220px] bg-card" placeholder={t("ai.ph")}
                  value={aiQ} onChange={(e) => setAiQ(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") askAi(); }} />
                <Button data-testid="ai-shop-btn" disabled={aiBusy} className="rounded-sm bg-accent text-white" onClick={askAi}>
                  {aiBusy ? t("ai.busy") : t("ai.btn")}
                </Button>
              </div>
              {aiRes && (
                <div className="mt-3" data-testid="ai-shop-results">
                  {(aiRes.product || (aiRes.keywords || []).length > 0) && (
                    <p className="text-xs text-muted-foreground" data-testid="ai-shop-understood">
                      {t("ai.understood")} : <b>{aiRes.product || aiRes.keywords.join(", ")}</b>
                      {aiRes.category ? ` · ${aiRes.category}` : ""}
                      {aiRes.product && aiRes.keywords?.length > 0 ? ` — ${aiRes.keywords.join(", ")}` : ""}
                    </p>
                  )}
                  {aiRes.message && <p className="text-sm font-medium" data-testid="ai-shop-message">{aiRes.message}</p>}
                  {aiRes.shops.length > 0 && (
                    <div className="mt-2 grid gap-2 grid-cols-2 md:grid-cols-3">
                      {aiRes.shops.map((s) => (
                        <div key={s.id} data-testid={`ai-shop-${s.name}`} className="border border-accent/50 bg-card p-3">
                          <div className="flex items-center gap-2">
                            {s.logo ? <img src={s.logo} alt="" className="h-6 w-6 object-contain" /> : <Store className="h-4 w-4 text-accent" />}
                            <span className="font-heading font-bold text-sm">{s.name}</span>
                          </div>
                          <div className="text-xs text-muted-foreground">{s.category}</div>
                          <div className="mt-2 flex gap-1.5">
                            <a href={s.url} target="_blank" rel="noopener noreferrer" className="flex-1">
                              <Button size="sm" variant="outline" className="w-full rounded-sm gap-1 text-xs">{t("ss.visit")} <ExternalLink className="h-3 w-3" /></Button>
                            </a>
                            {user?.role === "customer" && (
                              <Button size="sm" data-testid={`ai-declare-${s.name}`} className="rounded-sm bg-accent text-white text-xs"
                                onClick={() => { setDeclShop(s.name); setDeclOpen(true); }}>{t("ss.declared")}</Button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  {aiRes.shops.length === 0 && (
                    <Button size="sm" variant="outline" className="mt-2 rounded-sm" onClick={() => setOtherOpen(true)}>{t("ss.unlistedBtn")}</Button>
                  )}
                </div>
              )}
            </div>

            <div className="mt-3 flex flex-wrap gap-1.5">
              {CATEGORIES.map((c) => (
                <button key={c} onClick={() => setCat(c)} className={`px-3 py-1 text-xs font-semibold border ${cat === c ? "bg-primary text-white border-primary" : "border-border hover:border-accent"}`}>{t(`cat.${c}`)}</button>
              ))}
            </div>
            <div className="mt-4 grid gap-3 grid-cols-2 md:grid-cols-3 lg:grid-cols-5">
              {filteredShops.map((s) => (
                <div key={s.id} data-testid={`ss-shop-${s.name}`} className="border border-border bg-card p-4 flex flex-col">
                  <div className="flex items-center gap-2">
                    {s.logo ? <img src={s.logo} alt="" className="h-8 w-8 object-contain" /> : <Store className="h-6 w-6 text-accent" />}
                    <span className="font-heading font-bold">{s.name}</span>
                  </div>
                  <div className="mt-1 text-xs text-muted-foreground">{s.category}</div>
                  <a href={s.url} target="_blank" rel="noopener noreferrer" className="mt-3">
                    <Button size="sm" variant="outline" className="w-full rounded-sm gap-1 text-xs">{t("ss.visit")} <ExternalLink className="h-3 w-3" /></Button>
                  </a>
                </div>
              ))}
              {filteredShops.length === 0 && <p className="text-sm text-muted-foreground col-span-5">{t("ss.noShops")}</p>}
            </div>
            <div className="mt-5 border border-dashed border-border bg-card p-4 flex flex-wrap items-center justify-between gap-3" data-testid="unlisted-shop-block">
              <span className="text-sm text-muted-foreground">{t("ss.unlistedQ")}</span>
              <Button size="sm" variant="outline" data-testid="use-other-shop-btn" className="rounded-sm" onClick={() => setOtherOpen(true)}>{t("ss.unlistedBtn")}</Button>
            </div>
            <p className="mt-4 text-xs text-muted-foreground">{t("ss.disclaimer")}</p>
          </div>
        )}

        {/* Après achat */}
        {buy && user?.role === "customer" && (
          <div className="mt-10 border border-border bg-card p-6 flex flex-wrap items-center justify-between gap-3">
            <div>
              <div className="font-heading font-bold">{t("ss.followTitle")}</div>
              <p className="text-sm text-muted-foreground">{t("ss.followDesc")}</p>
            </div>
            <Button data-testid="ss-goto-purchases" variant="outline" className="rounded-sm gap-1.5" onClick={() => navigate("/dashboard?tab=purchases")}>{t("ss.myPurchases")} <ArrowRight className="h-4 w-4" /></Button>
          </div>
        )}
      </div>

      <CountrySearchDialog open={searchOpen} onOpenChange={setSearchOpen} onSelect={(c) => pick(c.name)} />

      <UnlistedShopDialog open={otherOpen} onOpenChange={setOtherOpen}
        onUse={(name) => { setOtherOpen(false); setDeclShop(name); setDeclOpen(true); }} />

      <DeclarePurchaseDialog open={declOpen} onOpenChange={setDeclOpen} country={route?.address_country || buy}
        warehouseId={route?.warehouse?.id || ""} shops={shops} initialShop={declShop}
        destCountries={dests} />
    </div>
  );
}

export function UnlistedShopDialog({ open, onOpenChange, onUse }) {
  const { t } = useI18n();
  const [name, setName] = useState("");
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);

  const lookup = async () => {
    if (name.trim().length < 2) { toast.error("Saisissez le nom de la boutique"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/shopship/shops/lookup", { name: name.trim() });
      setRes(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur de recherche"); }
    finally { setBusy(false); }
  };

  const registerShop = async (url) => {
    try {
      await api.post("/shopship/shops/unlisted", { name: name.trim(), url: url || "" });
      toast.success(url ? "Boutique enregistrée — site vérifié" : "Boutique enregistrée — URL à vérifier par Colis2Go");
      setName(""); setRes(null);
      onUse(name.trim());
    } catch (e) { toast.error(e.response?.data?.detail || "Connectez-vous en tant que client"); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) { setName(""); setRes(null); } }}>
      <DialogContent data-testid="unlisted-shop-dialog">
        <DialogHeader>
          <DialogTitle>{t("unl.title")}</DialogTitle>
          <DialogDescription>{t("unl.desc")}</DialogDescription>
        </DialogHeader>
        <div className="flex gap-2">
          <Input data-testid="other-shop-name" className="rounded-sm" placeholder="Selfridges, Next…" value={name}
            onChange={(e) => { setName(e.target.value); setRes(null); }} onKeyDown={(e) => e.key === "Enter" && lookup()} />
          <Button data-testid="other-shop-search" className="rounded-sm bg-primary text-white" onClick={lookup} disabled={busy}>{busy ? t("unl.searching") : t("unl.search")}</Button>
        </div>
        {res && (
          <div className="space-y-2">
            {res.catalog_matches.map((s) => (
              <div key={s.id} className="flex items-center justify-between border border-border p-2.5 text-sm">
                <span><b>{s.name}</b> <span className="text-muted-foreground">· {t("unl.inCatalog")}</span></span>
                <Button size="sm" data-testid={`use-catalog-${s.id}`} className="rounded-sm h-7 text-xs bg-accent text-white" onClick={() => { onUse(s.name); setName(""); setRes(null); }}>{t("unl.useThis")}</Button>
              </div>
            ))}
            {res.suggestions.map((s, i) => (
              <div key={i} className="flex items-center justify-between border border-emerald-300 bg-emerald-50 p-2.5 text-sm">
                <span><b>{name.trim()}</b> — {t("unl.found")}<div className="text-xs text-muted-foreground">{s.url}</div></span>
                <Button size="sm" data-testid={`use-suggestion-${i}`} className="rounded-sm h-7 text-xs bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => registerShop(s.url)}>{t("unl.useThis")}</Button>
              </div>
            ))}
            {res.catalog_matches.length === 0 && res.suggestions.length === 0 && (
              <p className="text-sm text-muted-foreground">{t("unl.noneFound")}</p>
            )}
            <Button variant="outline" data-testid="continue-without-url" className="w-full rounded-sm" onClick={() => registerShop("")}>{t("unl.noUrl")}</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function DeclarePurchaseDialog({ open, onOpenChange, country, warehouseId, shops = [], initialShop = "", destCountries = [], onDone }) {
  const { t } = useI18n();
  const destinations = destCountries.map((c) => c.name);
  const empty = { shop_name: initialShop, order_number: "", tracking_number: "", description: "", packages_expected: 1, declared_value: 0, dest_country: "", dest_city: "", mode: "", departure_id: "", notes: "" };
  const [f, setF] = useState(empty);
  React.useEffect(() => { setF((x) => ({ ...x, shop_name: initialShop })); }, [initialShop]);
  const submit = async () => {
    if (!f.shop_name) { toast.error("Boutique requise"); return; }
    try {
      const { data } = await api.post("/shopship/purchases", {
        ...f, country, warehouse_id: warehouseId || "",
        packages_expected: Number(f.packages_expected) || 1, declared_value: Number(f.declared_value) || 0,
      });
      toast.success("Achat déclaré — l’entrepôt est prévenu");
      setF(empty); onOpenChange(false); if (onDone) onDone(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t("ssd.title")}</DialogTitle>
          <DialogDescription>{t("ssd.recvDesc").replace("{c}", country)}</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2"><Label>{t("ssd.shop")}</Label>
            <Input data-testid="ssd-shop" className="rounded-sm" list="ss-shops-list" placeholder="Zara, Amazon…" value={f.shop_name} onChange={(e) => setF({ ...f, shop_name: e.target.value })} />
            <datalist id="ss-shops-list">{shops.map((s) => <option key={s.id} value={s.name} />)}</datalist>
          </div>
          <div><Label>{t("ssd.order")}</Label><Input data-testid="ssd-order" className="rounded-sm" value={f.order_number} onChange={(e) => setF({ ...f, order_number: e.target.value })} /></div>
          <div><Label>{t("ssd.tracking")}</Label><Input data-testid="ssd-tracking" className="rounded-sm" value={f.tracking_number} onChange={(e) => setF({ ...f, tracking_number: e.target.value })} /></div>
          <div><Label>{t("ssd.expected")}</Label><Input data-testid="ssd-count" type="number" className="rounded-sm" value={f.packages_expected} onChange={(e) => setF({ ...f, packages_expected: e.target.value })} /></div>
          <div><Label>{t("ssd.value")}</Label><Input type="number" className="rounded-sm" value={f.declared_value} onChange={(e) => setF({ ...f, declared_value: e.target.value })} /></div>
          <div className="col-span-2"><Label>{t("ssd.dest")}</Label>
            {destinations.length > 0 ? (
              <Select value={f.dest_country} onValueChange={(v) => setF({ ...f, dest_country: v, dest_city: "" })}>
                <SelectTrigger data-testid="ssd-dest" className="rounded-sm"><SelectValue placeholder={t("ssd.chooseDest")} /></SelectTrigger>
                <SelectContent>{destinations.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
              </Select>
            ) : (
              <Input data-testid="ssd-dest-input" className="rounded-sm" placeholder={t("ssd.destPh")} value={f.dest_country} onChange={(e) => setF({ ...f, dest_country: e.target.value, dest_city: "" })} />
            )}
          </div>
          {f.dest_country && (
            <div className="col-span-2">
              <DestCity country={destCountries.find((c) => c.name === f.dest_country)}
                value={f.dest_city} onChange={(v) => setF((x) => ({ ...x, dest_city: v }))} testid="ssd-city" />
            </div>
          )}
          {f.dest_country && (
            <div className="col-span-2">
              {/* Mode + départ planifié — même composant que « J'ai déjà ma marchandise » (Phase 4) */}
              <ModeDeparture hubCountry={country} destCountry={f.dest_country} mode={f.mode}
                onModeChange={(m) => setF((x) => ({ ...x, mode: m, departure_id: "" }))}
                departureId={f.departure_id}
                onDepartureChange={(v) => setF((x) => ({ ...x, departure_id: v }))} testid="ssd" />
            </div>
          )}
          <div className="col-span-2"><Label>{t("ssd.desc")}</Label><Textarea data-testid="ssd-desc" className="rounded-sm" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} /></div>
          <div className="col-span-2"><Label>{t("ssd.notes")}</Label><Input className="rounded-sm" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} /></div>
        </div>
        <DialogFooter><Button data-testid="ssd-submit" className="rounded-sm bg-accent text-white" onClick={submit}>{t("ssd.submit")}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
